// src/services/bookingService.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps-api.novprayan.com/api/public';
const NOVPRAYAN_PUBLIC_API_BASE_URL =
  process.env.REACT_APP_NOVPRAYAN_PUBLIC_API_URL || 'https://apps-api.novprayan.com/api/public';
const NOVPRAYAN_TENANT_ID = process.env.REACT_APP_NOVPRAYAN_TENANT_ID;
const NOVPRAYAN_COMPANY_CODE = process.env.REACT_APP_NOVPRAYAN_COMPANY_CODE || 'PL';
const NOVPRAYAN_RECAPTCHA_TOKEN = process.env.REACT_APP_NOVPRAYAN_RECAPTCHA_TOKEN;
const NOVPRAYAN_RECAPTCHA_SCORE = process.env.REACT_APP_NOVPRAYAN_RECAPTCHA_SCORE;
let hasWarnedAboutMissingRecaptcha = false;

const getNovprayanHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (NOVPRAYAN_TENANT_ID) {
    headers['X-Tenant-ID'] = NOVPRAYAN_TENANT_ID;
  } else if (NOVPRAYAN_COMPANY_CODE) {
    headers['X-Company-Code'] = NOVPRAYAN_COMPANY_CODE;
  }

  return headers;
};

const getTrackingContext = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);

  return {
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    enquiryClientAgent: navigator.userAgent || undefined,
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
};

const normalizePhoneNumber = (phone = '') => {
  const digits = String(phone).replace(/\D/g, '');

  if (digits.length === 10) {
    return `(+91) ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (digits.length === 12 && digits.startsWith('91')) {
    const localNumber = digits.slice(2);
    return `(+91) ${localNumber.slice(0, 5)} ${localNumber.slice(5)}`;
  }

  return phone;
};

const deriveVehicleTypes = (values = []) => {
  const mapped = values
    .map((value) => String(value || '').toUpperCase())
    .map((value) => {
      if (['BUS', 'SUV', 'SEDAN', 'HATCHBACK'].includes(value)) return value;
      if (value.includes('BUS')) return 'BUS';
      if (value.includes('SUV') || value.includes('INNOVA') || value.includes('ERTIGA') || value.includes('CARENS')) return 'SUV';
      if (value.includes('TEMPO') || value.includes('TRAVELLER') || value.includes('URBANIA')) return 'BUS';
      if (value.includes('ETIOS')) return 'ETIOS';
      if (value.includes('SEDAN')) return 'SEDAN';
      if (value.includes('HATCHBACK')) return 'HATCHBACK';
      return null;
    })
    .filter(Boolean);

  return [...new Set(mapped)];
};

const sanitizeArray = (values = []) => values.filter((value) => value !== undefined && value !== null && value !== '');

const sanitizeLongArray = (values = []) =>
  sanitizeArray(values)
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);

const sanitizeObject = (value = {}) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
  );

const sanitizeFormDataValue = (value) => {
  if (value === undefined || value === '') {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .map(sanitizeFormDataValue)
      .filter((item) => item !== undefined);

    return sanitizedItems.length ? sanitizedItems : undefined;
  }

  if (typeof value === 'object') {
    const sanitizedEntries = Object.entries(value)
      .map(([key, item]) => [key, sanitizeFormDataValue(item)])
      .filter(([, item]) => item !== undefined);

    return sanitizedEntries.length ? Object.fromEntries(sanitizedEntries) : undefined;
  }

  return value;
};

const sanitizeFormFields = (value = {}) => sanitizeFormDataValue(value) || {};

const toOptionalNumber = (value) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const toPositiveInteger = (value) => {
  const numberValue = Number(value);
  return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
};

const looksLikeApiVehicle = (vehicle = {}) =>
  Boolean(
    vehicle.registrationNumber ||
      vehicle.vehicleType ||
      vehicle.vehicleCategory ||
      Array.isArray(vehicle.rateCards)
  );

export const toVehicleBackendId = (vehicle = {}) => {
  const candidates = [
    vehicle.backendVehicleId,
    vehicle.vehicleId,
    vehicle.apiVehicle?.vehicleId,
    vehicle.apiVehicle?.backendVehicleId,
    vehicle.apiVehicle?.id,
    vehicle.vehicle?.vehicleId,
    vehicle.vehicle?.backendVehicleId,
    vehicle.vehicle?.id,
    vehicle.rawVehicle?.vehicleId,
    vehicle.rawVehicle?.backendVehicleId,
    vehicle.rawVehicle?.id,
    vehicle.sourceVehicle?.vehicleId,
    vehicle.sourceVehicle?.backendVehicleId,
    vehicle.sourceVehicle?.id,
    String(vehicle.catalogKey || '').match(/^id-(\d+)$/)?.[1],
  ];

  for (const candidate of candidates) {
    const id = toPositiveInteger(candidate);

    if (id !== null) {
      return id;
    }
  }

  const rawApiId = looksLikeApiVehicle(vehicle) ? toPositiveInteger(vehicle.id) : null;
  if (rawApiId !== null) {
    return rawApiId;
  }

  return null;
};

const toFormDataString = (formData, fallbackFields) => {
  if (typeof formData === 'string') {
    return formData.trim() || undefined;
  }

  const source = formData || fallbackFields;

  if (!source || Object.keys(source).length === 0) {
    return undefined;
  }

  const sanitizedSource = sanitizeFormFields(source);

  if (Object.keys(sanitizedSource).length === 0) {
    return undefined;
  }

  return JSON.stringify(sanitizedSource);
};

export const normalizeBookingType = (value = '') => {
  const type = String(value || '').toLowerCase();

  if (type.includes('airport')) return 'Airport Taxi';
  if (type.includes('outstation')) return 'Outstation';
  if (type.includes('local')) return 'Local Taxi';

  return value || '';
};

const parseMoney = (value) => {
  const numberValue = Number(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
};

const normalizeRateCardTripType = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const getRateCardBaseRate = (rateCard = {}) => (
  rateCard.baseRate ?? rateCard.rate ?? rateCard.basePrice
);

const hasPositiveBaseRate = (rateCard = {}) => {
  const baseRate = Number(getRateCardBaseRate(rateCard));
  return Number.isFinite(baseRate) && baseRate > 0;
};

const getVehicleRateCards = (vehicle = {}) => [
  ...(Array.isArray(vehicle.rateCards) ? vehicle.rateCards : []),
  ...(Array.isArray(vehicle.apiVehicle?.rateCards) ? vehicle.apiVehicle.rateCards : []),
  ...(Array.isArray(vehicle.rawVehicle?.rateCards) ? vehicle.rawVehicle.rateCards : []),
  ...(Array.isArray(vehicle.sourceVehicle?.rateCards) ? vehicle.sourceVehicle.rateCards : []),
  ...(Array.isArray(vehicle.vehicle?.rateCards) ? vehicle.vehicle.rateCards : []),
];

export const getAirportRateCardTripType = (airportTripType = '') => {
  const value = String(airportTripType || '').toLowerCase();
  return value.includes('pickup') || value.includes('airporttocity') || value.includes('airport to city')
    ? 'Airport PickUp'
    : 'Airport Drop';
};

export const findRateCardByTripType = (rateCards = [], tripType) => {
  const normalizedTripType = normalizeRateCardTripType(tripType);
  if (!normalizedTripType) return null;

  const matchingRateCards = rateCards.filter(
    (rateCard) => normalizeRateCardTripType(rateCard.tripType) === normalizedTripType
  );

  return matchingRateCards.find(hasPositiveBaseRate) || matchingRateCards[0] || null;
};

const resolveSelectedRateCard = (selectedVehicle = {}, pricing = {}, bookingType, rateCardTripType) => {
  const availableRateCards = getVehicleRateCards(selectedVehicle);
  const matchedRateCard = findRateCardByTripType(availableRateCards, rateCardTripType);

  if (matchedRateCard) return matchedRateCard;

  return (
    selectedVehicle.selectedRateCard ||
    selectedVehicle.rateCard ||
    selectedVehicle.packageDetails ||
    pricing.packageDetails ||
    findRateCardByTripType(availableRateCards, normalizeBookingType(bookingType)) ||
    {}
  );
};

const compactVehicleDetails = (vehicle = {}, selectedRateCard) =>
  sanitizeObject({
    id: toVehicleBackendId(vehicle),
    vehicleId: toVehicleBackendId(vehicle),
    registrationNumber: vehicle.registrationNumber,
    vehicleType: vehicle.vehicleType,
    vehicleCategory: vehicle.vehicleCategory,
    fuelType: vehicle.fuelType,
    year: vehicle.year,
    name: vehicle.name,
    models: vehicle.models,
    category: vehicle.category,
    type: vehicle.type,
    seaters: vehicle.seaters ?? vehicle.seats,
    bags: vehicle.bags,
    price: vehicle.price,
    per: vehicle.per,
    selectedPackage: vehicle.selectedPackage,
    features: Array.isArray(vehicle.features) ? vehicle.features : undefined,
    tag: vehicle.tag,
    rating: vehicle.rating,
  });

const buildSelectedVehicleFormItem = (vehicle = {}, rateCard = {}, bookingType) => {
  const vehicleId = toVehicleBackendId(vehicle);
  const selectedTripType = String(rateCard.tripType || bookingType || '').toUpperCase();

  return sanitizeObject({
    id: vehicleId,
    registrationNumber: vehicle.registrationNumber,
    vehicleType: vehicle.vehicleType || vehicle.type || vehicle.category,
    vehicleCategory: vehicle.vehicleCategory || vehicle.category,
    capacity: vehicle.capacity || vehicle.seats || vehicle.seaters,
    manufacturer: vehicle.manufacturer,
    fuelType: vehicle.fuelType,
    selectedRateCard: sanitizeObject({
      packageId: rateCard.packageId,
      packageName: rateCard.packageName,
      tripType: selectedTripType,
      baseRate: toOptionalNumber(rateCard.baseRate),
      extraHourRate: toOptionalNumber(rateCard.extraHourRate),
      extraDayRate: toOptionalNumber(rateCard.extraDayRate),
      extraKmRate: toOptionalNumber(rateCard.extraKmRate),
      maxHours: toOptionalNumber(rateCard.maxHours),
      maxDays: toOptionalNumber(rateCard.maxDays),
      maxKilometers: toOptionalNumber(rateCard.maxKilometers),
      driverBata: toOptionalNumber(rateCard.driverBata),
      location: rateCard.location,
      validFrom: rateCard.validFrom,
      validTo: rateCard.validTo,
    }),
    qty: 1,
    acNonAC: vehicle.acNonAC || 'AC',
    selectedPackageName: rateCard.packageName,
    selectedTripType,
    selectedLocation: rateCard.location,
    selectedBaseRate: toOptionalNumber(rateCard.baseRate),
    rateCardValidity:
      rateCard.validFrom || rateCard.validTo
        ? sanitizeObject({
            from: rateCard.validFrom,
            to: rateCard.validTo,
          })
        : undefined,
  });
};

export const buildSelectedVehiclePayloadContext = ({
  vehicle,
  initialRouteData,
  bookingType,
  rateCardTripType,
} = {}) => {
  const route = initialRouteData?.route || {};
  const pricing = initialRouteData?.pricing || {};
  const selectedVehicle = vehicle || initialRouteData?.vehicle || {};
  const selectedRateCard = resolveSelectedRateCard(selectedVehicle, pricing, bookingType, rateCardTripType);
  const vehicleDetails = compactVehicleDetails(selectedVehicle, selectedRateCard);
  const packageDetails = selectedRateCard || selectedVehicle.packageDetails || pricing.packageDetails;
  const selectedPackage = selectedRateCard.packageName || selectedVehicle.selectedPackage || pricing.selectedPackage;
  const selectedVehicleId = toVehicleBackendId(selectedVehicle);
  const rateCard = sanitizeObject({
    routeName: route.routeName,
    pickup: route.pickup,
    dropoff: route.dropoff,
    distance: route.distance,
    estimatedTime: route.estimatedTime,
    price: route.price || selectedVehicle.price,
    tripDirection: route.tripDirection,
    tripType: selectedRateCard.tripType || route.tripType || normalizeBookingType(bookingType),
    basePrice: selectedRateCard.baseRate || pricing.basePrice || selectedVehicle.price,
    includedKms: selectedRateCard.maxKilometers || pricing.includedKms || packageDetails?.kms,
    includedHours: selectedRateCard.maxHours || pricing.includedHours || packageDetails?.hours,
    extraPerKm: selectedRateCard.extraKmRate || pricing.extraPerKm || packageDetails?.extraKm,
    extraPerHour: selectedRateCard.extraHourRate || pricing.extraPerHour || packageDetails?.extraHr,
    driverBata: selectedRateCard.driverBata || pricing.driverBata || packageDetails?.driverBata,
    selectedRateCard,
    tolls: pricing.tolls,
    selectedPackage,
    packageDetails: selectedRateCard,
  });

  return {
    vehicleDetails,
    rateCard,
    selectedVehicleId,
    selectedVehicleIds:
      selectedVehicleId !== null ? [selectedVehicleId] : [],
    selectedVehicleTypes: [
      selectedVehicle.vehicleType,
      selectedVehicle.category,
      selectedVehicle.type,
      selectedVehicle.name,
      selectedVehicle.models,
      bookingType,
    ].filter(Boolean),
    expectedPax: selectedVehicle.seats || selectedVehicle.seaters,
    expectedBudget: parseMoney(rateCard.price || rateCard.basePrice || selectedVehicle.price),
    packageLabel:
      packageDetails?.label ||
      [packageDetails?.hours, packageDetails?.kms].filter(Boolean).join(' / ') ||
      selectedPackage,
    selectedVehicles: Object.keys(vehicleDetails).length
      ? [buildSelectedVehicleFormItem(selectedVehicle, selectedRateCard, bookingType)]
      : [],
  };
};

const extractEnquiryReference = (alertHeader = '') => {
  const match = String(alertHeader).match(/"(.*?)"/);
  return match?.[1] || undefined;
};

const getSecurityContext = () => {
  if (typeof window === 'undefined') {
    return {
      recaptchaToken: NOVPRAYAN_RECAPTCHA_TOKEN,
      recaptchaScore: NOVPRAYAN_RECAPTCHA_SCORE,
    };
  }

  const globalSecurity = window.__NOVPRAYAN_RECAPTCHA__ || {};

  return {
    recaptchaToken:
      globalSecurity.token ||
      window.__NOVPRAYAN_RECAPTCHA_TOKEN__ ||
      window.sessionStorage?.getItem('novprayanRecaptchaToken') ||
      window.localStorage?.getItem('novprayanRecaptchaToken') ||
      NOVPRAYAN_RECAPTCHA_TOKEN,
    recaptchaScore:
      globalSecurity.score ??
      window.__NOVPRAYAN_RECAPTCHA_SCORE__ ??
      window.sessionStorage?.getItem('novprayanRecaptchaScore') ??
      window.localStorage?.getItem('novprayanRecaptchaScore') ??
      NOVPRAYAN_RECAPTCHA_SCORE,
  };
};

export const buildWebsiteEnquiryPayload = ({
  customerName,
  customerEmail,
  customerPhone,
  subject,
  message,
  expectedTravelDate,
  expectedPax,
  expectedBudget,
  selectedVehicleIds = [],
  selectedVehicleTypes = [],
  preferredVehicleCategory,
  pickupLocation,
  dropLocation,
  tripType,
  pageUrl,
  referrer,
  enquiryClientAgent,
  userAgent,
  ipAddress,
  utmSource,
  utmMedium,
  utmCampaign,
  recaptchaToken,
  recaptchaScore,
  formData,
  customFields = {},
  customFormFields,
}) => {
  const tracking = getTrackingContext();
  const security = getSecurityContext();
  const resolvedRecaptchaToken = recaptchaToken || security.recaptchaToken;
  const resolvedRecaptchaScore = recaptchaScore ?? security.recaptchaScore;
  const hasRecaptchaValues =
    Boolean(resolvedRecaptchaToken) &&
    resolvedRecaptchaScore !== undefined &&
    resolvedRecaptchaScore !== null &&
    resolvedRecaptchaScore !== '';

  if (!hasRecaptchaValues && !hasWarnedAboutMissingRecaptcha) {
    hasWarnedAboutMissingRecaptcha = true;
    console.warn(
      'NOVPRAYAN enquiry is being submitted without recaptchaToken/recaptchaScore. Add them via payload, window.__NOVPRAYAN_RECAPTCHA__, browser storage, or env vars REACT_APP_NOVPRAYAN_RECAPTCHA_TOKEN and REACT_APP_NOVPRAYAN_RECAPTCHA_SCORE if the API requires them.'
    );
  }

  const resolvedCustomFormFields = sanitizeFormFields(customFormFields || customFields);

  const payload = {
    customerName: customerName?.trim(),
    customerEmail: customerEmail?.trim(),
    customerPhone: normalizePhoneNumber(customerPhone),
    subject: subject?.trim(),
    message: message?.trim(),
    expectedTravelDate: expectedTravelDate || undefined,
    expectedPax: toOptionalNumber(expectedPax),
    expectedBudget: toOptionalNumber(expectedBudget),
    selectedVehicleIds: sanitizeLongArray(selectedVehicleIds),
    selectedVehicleTypes: deriveVehicleTypes(
      selectedVehicleTypes.length ? selectedVehicleTypes : selectedVehicleIds
    ),
    preferredVehicleCategory: preferredVehicleCategory || undefined,
    formData: toFormDataString(formData, resolvedCustomFormFields),
    pickupLocation: pickupLocation?.trim() || undefined,
    dropLocation: dropLocation?.trim() || undefined,
    tripType: tripType || undefined,
    ipAddress: ipAddress || undefined,
    enquiryClientAgent: enquiryClientAgent || userAgent || tracking.enquiryClientAgent,
    pageUrl: pageUrl || tracking.pageUrl,
    referrer: referrer || tracking.referrer,
    utmSource: utmSource || tracking.utmSource,
    utmMedium: utmMedium || tracking.utmMedium,
    utmCampaign: utmCampaign || tracking.utmCampaign,
    recaptchaToken: hasRecaptchaValues ? resolvedRecaptchaToken : undefined,
    recaptchaScore: hasRecaptchaValues ? toOptionalNumber(resolvedRecaptchaScore) : undefined,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== undefined && value !== null && value !== '';
    })
  );
};

export const bookingService = {
  createBooking: async (bookingData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Booking failed');
      }

      return data;
    } catch (error) {
      console.error('Booking API Error:', error);
      throw error;
    }
  },

  submitWebsiteEnquiry: async (enquiryPayload) => {
    try {
      const response = await fetch(`${NOVPRAYAN_PUBLIC_API_BASE_URL}/enquiries/website`, {
        method: 'POST',
        headers: getNovprayanHeaders(),
        body: JSON.stringify(enquiryPayload),
      });

      let data = null;
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        data = await response.json();
      }

      if (!response.ok) {
        throw new Error(data?.title || data?.message || data?.error || 'Website enquiry submission failed');
      }

      return (
        data || {
          success: true,
          status: response.status,
          enquiryReference: extractEnquiryReference(response.headers.get('X-NovprayanApp-Alert')),
        }
      );
    } catch (error) {
      console.error('Website Enquiry API Error:', error);
      throw error;
    }
  },

  getBooking: async (bookingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch booking');
      }

      return data;
    } catch (error) {
      console.error('Get Booking Error:', error);
      throw error;
    }
  },

  getBookings: async (filters = {}) => {
    try {
      const queryString = new URLSearchParams(filters).toString();
      const url = `${API_BASE_URL}/bookings${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch bookings');
      }

      return data;
    } catch (error) {
      console.error('Get Bookings Error:', error);
      throw error;
    }
  },

  updateBookingStatus: async (bookingId, status) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update booking');
      }

      return data;
    } catch (error) {
      console.error('Update Booking Error:', error);
      throw error;
    }
  },

  cancelBooking: async (bookingId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to cancel booking');
      }

      return data;
    } catch (error) {
      console.error('Cancel Booking Error:', error);
      throw error;
    }
  },

  calculateFare: async (tripDetails) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/calculate-fare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tripDetails),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to calculate fare');
      }

      return data;
    } catch (error) {
      console.error('Calculate Fare Error:', error);
      throw error;
    }
  },

  checkAvailability: async (availabilityData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/check-availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(availabilityData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to check availability');
      }

      return data;
    } catch (error) {
      console.error('Check Availability Error:', error);
      throw error;
    }
  }
};

export default bookingService;
